import { Input } from './input';
import { Label } from './label';
import { Textarea } from './textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Checkbox } from './checkbox';
import { isAnswerEmpty, type AnswerMap, type AnswerValue, type ValidatableField } from '../../utils/formFieldValidation';

export interface CustomQuestionField extends ValidatableField {
  fieldId?: string;
  placeholder?: string;
  section?: string;
  /** Small print beneath the question. */
  helpText?: string;
}

interface CustomQuestionsFieldsetProps {
  fields: CustomQuestionField[];
  answers: AnswerMap;
  errors: { [key: string]: string };
  onChange: (fieldName: string, value: AnswerValue) => void;
  /**
   * Ticking a multi-select option. Reports the option and whether it was
   * ticked, rather than the resulting array, so the parent can apply it against
   * the latest state. Computing the array here reads a value captured at render
   * time, which loses one of two options ticked in the same tick.
   */
  onToggleOption: (fieldName: string, option: string, checked: boolean) => void;
  /** Heading used when a question has no section of its own. */
  fallbackHeading?: string;
}

/**
 * Renders an event's custom registration questions, grouped by section.
 *
 * Shared so that a signed-in member and a guest answer exactly the same
 * questions in exactly the same way. Previously only the guest form rendered
 * these, which meant a form built by an admin was invisible to members.
 */
export default function CustomQuestionsFieldset({
  fields,
  answers,
  errors,
  onChange,
  onToggleOption,
  fallbackHeading = 'Additional Information',
}: CustomQuestionsFieldsetProps) {
  if (!fields || fields.length === 0) return null;

  const renderField = (field: CustomQuestionField) => {
    const key = `custom_${field.fieldName}`;
    const value = answers[field.fieldName];
    const fieldError = errors[key];
    const labelNode = (
      <>
        <Label htmlFor={key}>
          {field.fieldLabel}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        {field.helpText && (
          <p className="text-xs text-gray-500 -mt-1">{field.helpText}</p>
        )}
      </>
    );

    switch (field.fieldType) {
      case 'textarea':
        return (
          <div key={key} className="space-y-2">
            {labelNode}
            <Textarea
              id={key}
              placeholder={field.placeholder}
              value={(value as string) || ''}
              onChange={(e) => onChange(field.fieldName, e.target.value)}
              className={fieldError ? 'border-red-500' : ''}
            />
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );

      case 'select':
        return (
          <div key={key} className="space-y-2">
            {labelNode}
            <Select
              value={(value as string) || ''}
              onValueChange={(v) => onChange(field.fieldName, v)}
            >
              <SelectTrigger className={fieldError ? 'border-red-500' : ''}>
                <SelectValue placeholder={field.placeholder || 'Select an option'} />
              </SelectTrigger>
              <SelectContent>
                {(field.options || []).map((option) => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );

      case 'multiselect': {
        const selected = Array.isArray(value) ? value : [];
        return (
          <div key={key} className="space-y-2">
            {labelNode}
            <p className="text-xs text-gray-500">Select all that apply</p>
            <div className="space-y-2">
              {(field.options || []).map((option) => (
                <div key={option} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${key}_${option}`}
                    checked={selected.includes(option)}
                    onCheckedChange={(checked) => onToggleOption(field.fieldName, option, checked as boolean)}
                  />
                  <Label htmlFor={`${key}_${option}`} className="text-sm font-normal">
                    {option}
                  </Label>
                </div>
              ))}
            </div>
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );
      }

      case 'checkbox':
        return (
          <div key={key} className="space-y-1">
            <div className="flex items-center space-x-2">
              <Checkbox
                id={key}
                checked={value === true}
                onCheckedChange={(checked) => onChange(field.fieldName, checked as boolean)}
              />
              <Label htmlFor={key} className="text-sm font-normal">
                {field.fieldLabel}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
            </div>
            {field.helpText && <p className="text-xs text-gray-500 ml-6">{field.helpText}</p>}
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );

      case 'radio':
        return (
          <div key={key} className="space-y-2">
            {labelNode}
            <div className="space-y-2">
              {(field.options || []).map((option) => (
                <div key={option} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id={`${key}_${option}`}
                    name={key}
                    value={option}
                    checked={value === option}
                    onChange={(e) => onChange(field.fieldName, e.target.value)}
                    className="h-4 w-4"
                  />
                  <Label htmlFor={`${key}_${option}`} className="text-sm font-normal">
                    {option}
                  </Label>
                </div>
              ))}
            </div>
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );

      case 'date':
      case 'time':
      case 'number':
        return (
          <div key={key} className="space-y-2">
            {labelNode}
            <Input
              id={key}
              type={field.fieldType}
              placeholder={field.placeholder}
              value={(value as string) || ''}
              onChange={(e) => onChange(field.fieldName, e.target.value)}
              className={fieldError ? 'border-red-500' : ''}
              // Any number is accepted; a step of 'any' avoids the browser
              // rejecting decimals on a whole-number spinner.
              {...(field.fieldType === 'number' ? { step: 'any' } : {})}
            />
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );

      default: // text, email, phone
        return (
          <div key={key} className="space-y-2">
            {labelNode}
            <Input
              id={key}
              type={field.fieldType === 'email' ? 'email' : field.fieldType === 'phone' ? 'tel' : 'text'}
              placeholder={field.placeholder}
              value={(value as string) || ''}
              onChange={(e) => onChange(field.fieldName, e.target.value)}
              className={fieldError ? 'border-red-500' : ''}
            />
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );
    }
  };

  // Consecutive questions sharing a section name form one group, preserving the
  // order the admin arranged them in.
  const groups: Array<{ title: string; fields: CustomQuestionField[] }> = [];
  for (const field of fields) {
    const title = field.section?.trim() || fallbackHeading;
    const last = groups[groups.length - 1];
    if (last && last.title === title) last.fields.push(field);
    else groups.push({ title, fields: [field] });
  }

  // Answered counts drive the progress shown at the top of a long form.
  const answeredIn = (group: { fields: CustomQuestionField[] }) =>
    group.fields.filter((f) => !isAnswerEmpty(f, answers[f.fieldName])).length;
  const totalAnswered = groups.reduce((n, g) => n + answeredIn(g), 0);
  const totalQuestions = fields.length;
  const percent = totalQuestions === 0 ? 0 : Math.round((totalAnswered / totalQuestions) * 100);

  return (
    <div className="space-y-8">
      {/* A form of eight sections needs to show how much is left. */}
      {groups.length > 1 && (
        <div className="sticky top-0 z-10 -mx-1 rounded-md border border-gray-200 bg-white/95 px-4 py-3 backdrop-blur">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-sm font-medium text-gray-900">
              {totalAnswered} of {totalQuestions} answered
            </p>
            <p className="text-xs text-gray-500">{groups.length} sections</p>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300"
              style={{ width: `${percent}%` }}
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Registration form progress"
            />
          </div>
        </div>
      )}

      {groups.map((group, gi) => {
        const done = answeredIn(group);
        const complete = done === group.fields.length;
        return (
          <section
            key={`${group.title}-${gi}`}
            className="space-y-4 rounded-lg border border-gray-200 bg-white p-5"
            aria-labelledby={`section-${gi}`}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-100 pb-3">
              <div>
                <h3 id={`section-${gi}`} className="text-lg font-semibold">{group.title}</h3>
                {groups.length > 1 && (
                  <p className="mt-0.5 text-xs text-gray-500">
                    Section {gi + 1} of {groups.length}
                  </p>
                )}
              </div>
              {groups.length > 1 && (
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    complete ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {complete ? 'Complete' : `${done} of ${group.fields.length}`}
                </span>
              )}
            </div>
            {group.fields.map(renderField)}
          </section>
        );
      })}
    </div>
  );
}
